/**
 * 最简 fetch wrapper · Vite dev 走 /api 代理到后端 localhost:3000
 * 生产用 VITE_API_BASE_URL env 覆盖
 */
const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

export interface ApiError {
  code: string;
  message: string;
  errors?: Array<{ path: string; message: string }>;
  status: number;
}

export class ApiFailure extends Error {
  code: string;
  errors?: Array<{ path: string; message: string }>;
  status: number;

  constructor(err: ApiError) {
    super(err.message);
    this.code = err.code;
    this.errors = err.errors;
    this.status = err.status;
  }
}

function buildUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${BASE}${p}`;
}

let currentToken: string | null = null;
let onUnauthorized: () => void = () => {};
let onReacceptRequired: (missingVersionIds: string[]) => void = () => {};

export function setAuthToken(token: string | null): void {
  currentToken = token;
}

export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export function setReacceptRequiredHandler(fn: (missingVersionIds: string[]) => void): void {
  onReacceptRequired = fn;
}

export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (currentToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${currentToken}`);
  }

  let resp: Response;
  try {
    resp = await fetch(buildUrl(path), { ...init, headers });
  } catch (networkErr) {
    throw new ApiFailure({
      code: 'NETWORK_ERROR',
      message: '网络连接失败，请检查后端服务',
      status: 0,
    });
  }

  if (resp.status === 204) return undefined as T;

  let body: unknown = null;
  const text = await resp.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { code: 'UNKNOWN', message: text };
    }
  }

  if (!resp.ok) {
    if (resp.status === 401) onUnauthorized();
    const envelope = body as (Partial<ApiError> & { missingVersionIds?: string[] }) | null;
    if (resp.status === 403 && envelope?.code === 'AGR.REACCEPT_REQUIRED') {
      onReacceptRequired(envelope.missingVersionIds ?? []);
    }
    throw new ApiFailure({
      code: envelope?.code ?? 'HTTP_ERROR',
      message: envelope?.message ?? `请求失败（HTTP ${resp.status}）`,
      errors: envelope?.errors,
      status: resp.status,
    });
  }

  return body as T;
}
